import React, { useCallback, useEffect, useState } from 'react';
import { DataListItem, DataListItemRow, DataListItemCells, DataListCell, DataListAction, Dropdown, DropdownPosition, KebabToggle, Flex, FlexItem, TextInput, DropdownItem, DropdownToggle, Divider, Stack, StackItem, Alert } from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';
import { CogIcon } from '@patternfly/react-icons';


import { removeModelMetadataItem, removeScrollToViewFromModelCustomizationMetadata, StoreState, updateModelMetadataItem, } from '../../store/reducers';
import './customization-page.css';
import { useScroll } from '../../hooks/useScroll';
import InputLabel from '../../components/input-label';
import { selectCustomizationValidationError, selectModelCustomizationMetadataById, selectOriginalModelCustomizationMetadataById } from '../../store/selectors';
import { CustomizationObjectType, DEFAULT_FILENAME, METADATA_FILENAME_ATTRIBUTE } from '../../store/model-customization-types';
import { useTranslation } from 'react-i18next';
import { ensureFilenameHasDcmExtension } from './file-operations';

interface ModelMetadataItemFormProps {
    metadataId: string,
}

const ModelMetadataItemForm = (props: ModelMetadataItemFormProps) => {
    const { metadataId } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [isActionListOpen, setActionListOpenState] = useState(false);

    const metadataItem = useSelector((state: StoreState) => selectModelCustomizationMetadataById(state, metadataId));
    const validationError = useSelector((state: StoreState) => selectCustomizationValidationError(state, metadataId));
    const originalMetadataItem = useSelector((state: StoreState) => selectOriginalModelCustomizationMetadataById(state, metadataId));
    const [isPresetToggleOpen, setIsPresetToggleOpen] = useState(false);

    const handleUndo = useCallback(() => {
        dispatch(updateModelMetadataItem(metadataId, originalMetadataItem!.attribute, originalMetadataItem!.value, true));
    }, [dispatch, metadataId, originalMetadataItem]);

    const handleRemove = useCallback(() => {
        dispatch(removeModelMetadataItem(metadataId));
    }, [dispatch, metadataId]);

    const handleAttributeChange = useCallback((attribute: string) => {
        dispatch(updateModelMetadataItem(metadataId, attribute, undefined));
    }, [dispatch, metadataId]);

    const handleValueChange = useCallback((value: string) => {
        dispatch(updateModelMetadataItem(metadataId, undefined, value));
    }, [dispatch, metadataId]);

    const handleBlur = useCallback((e?: never, attribute?: string) => {

        // optional 'attribute' argument allows us to call this function more easily
        // before we've applied updates to store
        const attrib = attribute || metadataItem?.attribute;

        // make sure a file name has an extension as we leave the matching input
        if (metadataItem && attrib === METADATA_FILENAME_ATTRIBUTE) {
            const updatedFilename = ensureFilenameHasDcmExtension(metadataItem.value || DEFAULT_FILENAME);
            if (updatedFilename !== metadataItem.value) {
                dispatch(updateModelMetadataItem(metadataId, undefined, updatedFilename));
            }
        }
    }, [dispatch, metadataId, metadataItem]);

    const handleToggleActionListOpen = useCallback(() => setActionListOpenState(!isActionListOpen), [isActionListOpen]);

    const [executeScroll, scrollRef] = useScroll<HTMLDivElement>(true);
    const [isHighlightedItem, setIsHighlightedItem] = useState(false);

    useEffect(() => {
        if (metadataItem && scrollRef && metadataItem.scrollToView) {
            setIsHighlightedItem(true);
            dispatch(removeScrollToViewFromModelCustomizationMetadata(metadataId));
            executeScroll();
        }
    }, [executeScroll, scrollRef, dispatch, metadataItem, metadataId]);

    const handleSelectPreset = useCallback((dicomAttributeName: string) => {
        handleAttributeChange(dicomAttributeName);
        setIsPresetToggleOpen(false);
        handleBlur(undefined, dicomAttributeName);
    }, [handleAttributeChange, handleBlur]);

    if (metadataItem === undefined) {
        return null;
    }

    const metadataAttributePresets = [
        <DropdownItem data-test='customization-metadata-dropdown' data-testid='title' key="title" isPlainText>{t('customizeMetadata.presetLabel')}</DropdownItem>,
        <DropdownItem data-test='customization-metadata-dropdown' data-testid='divider' key="divider" isPlainText><Divider /></DropdownItem>,
        <DropdownItem data-test='customization-metadata-dropdown' data-testid='filename' key="filename" onClick={() => handleSelectPreset(METADATA_FILENAME_ATTRIBUTE)}>{METADATA_FILENAME_ATTRIBUTE}</DropdownItem>,
        <DropdownItem data-test='customization-metadata-dropdown' data-testid='series-description' key="SeriesDescription" onClick={() => handleSelectPreset('SeriesDescription')}>SeriesDescription</DropdownItem>,
        <DropdownItem data-test='customization-metadata-dropdown' data-testid='structure-set-label' key="StructureSetLabel" onClick={() => handleSelectPreset('StructureSetLabel')}>StructureSetLabel</DropdownItem>,
    ];

    const prefix = `metadata-${metadataId}`;
    const attributeId = `${prefix}-attribute`;
    const valueId = `${prefix}-value`;

    const highlight = isHighlightedItem ? 'highlighted-item' : '';

    const hasValidationError = validationError !== undefined && validationError.type === CustomizationObjectType.Metadata;

    return (
        <DataListItem aria-labelledby={`Metadata configuration for metadata item ${metadataId}`} key={metadataId}>
            <Stack>
                <StackItem>
                    <DataListItemRow className="roi-customization">

                        <DataListItemCells dataListCells={[
                            <DataListCell key="structure-customizations" className={`structure-customizations ${hasValidationError ? 'validation-error' : ''}`}>
                                <div ref={scrollRef} className="metadata-scroll-offset">
                                    {metadataItem.isModified && <div className="roi-item-is-modified" title="This metadata item has unsaved changes.">*</div>}
                                    <Flex className={`customization-row ${highlight}`}>
                                        <FlexItem className="metadata-attribute">
                                            <InputLabel label={t('customizeMetadata.attribute')} useRef>
                                                <TextInput
                                                    data-test='customization-metadata'
                                                    data-testid='attribute'
                                                    type="text"
                                                    id={attributeId}
                                                    onChange={handleAttributeChange}
                                                    onBlur={handleBlur}
                                                    value={metadataItem.attribute}
                                                />
                                                <Dropdown
                                                    data-test='customization-metadata'
                                                    data-testid='attribute-dropdown'
                                                    dropdownItems={metadataAttributePresets}
                                                    isOpen={isPresetToggleOpen}
                                                    title="Optional preset metadata attribute names"
                                                    toggle={<DropdownToggle toggleIndicator={null} onToggle={() => setIsPresetToggleOpen(!isPresetToggleOpen)}><CogIcon /></DropdownToggle>}
                                                />
                                            </InputLabel>
                                        </FlexItem>
                                        <FlexItem className="mv-flex-grow metadata-value">
                                            <InputLabel label={t('customizeMetadata.value')} useRef>
                                                <TextInput
                                                    data-test='customization-metadata'
                                                    data-testid='value'
                                                    type="text"
                                                    id={valueId}
                                                    onChange={handleValueChange}
                                                    onBlur={handleBlur}
                                                    value={metadataItem.value}
                                                />
                                            </InputLabel>
                                        </FlexItem>
                                    </Flex>
                                </div>
                            </DataListCell>
                        ]} />
                        <DataListAction id={`actions-for-${metadataId}`} aria-label={`Actions for metadata item ${metadataId}`} aria-labelledby="" isPlainButtonAction>
                            <Dropdown
                                data-test='customization-metadata'
                                data-testid='action-dropdown'
                                isPlain
                                position={DropdownPosition.right}
                                isOpen={isActionListOpen}
                                onSelect={handleToggleActionListOpen}
                                toggle={<KebabToggle onToggle={handleToggleActionListOpen} />}
                                dropdownItems={[
                                    <DropdownItem
                                        data-test='customization-metadata-dropdown'
                                        data-testid='undo'
                                        key="undo-metadata-changes"
                                        component="button"
                                        isDisabled={originalMetadataItem === undefined || !metadataItem.isModified}
                                        onClick={handleUndo}>{t('customizeMetadata.undoChanges')}</DropdownItem>,
                                    <DropdownItem
                                        data-test='customization-metadata-dropdown'
                                        data-testid='remove'
                                        key="remove-metadata-item"
                                        component="button"
                                        onClick={handleRemove}>{t('customizeMetadata.removeItem')}</DropdownItem>,
                                ]}
                            />
                        </DataListAction>

                    </DataListItemRow>
                </StackItem>
                {hasValidationError && (
                    <StackItem className="validation-error-box">
                        <Alert variant="danger" isInline isPlain isExpandable title={`Validation error: ${validationError.message}`}>
                            <div>Error type: {validationError.detail}</div>
                            <div>Error ctx: {validationError.ctx}</div>
                        </Alert>
                    </StackItem>
                )}
            </Stack>
        </DataListItem>

    );
}

export default ModelMetadataItemForm;
